import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MessageSquare, Briefcase, Calendar, Users, Megaphone, Laptop, Settings } from 'lucide-react'; // Added more icons

const VirtualAssistance = () => {
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');

    // The CORRECT Google Form entry IDs for Email and How can we help?
    const GOOGLE_FORM_EMAIL_ENTRY_ID = 'entry.111788535';
    const GOOGLE_FORM_MESSAGE_ENTRY_ID = 'entry.1822644906';

    // The Google Form submission endpoint
    const GOOGLE_FORM_ACTION_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdxfx2Vrx93tzACIANcSK1fy4T5HXXKYN11oJGBxOsUHEk80w/formResponse';

    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevent default form submission
        setIsSubmitting(true);
        setSubmitMessage('');

        // Create FormData object
        const formData = new FormData();
        formData.append(GOOGLE_FORM_EMAIL_ENTRY_ID, email);
        formData.append(GOOGLE_FORM_MESSAGE_ENTRY_ID, message);

        try {
            // Send the POST request to Google Forms
            const response = await fetch(GOOGLE_FORM_ACTION_URL, {
                method: 'POST',
                mode: 'no-cors', // Important: Google Forms has CORS restrictions, 'no-cors' is needed for a successful request from client-side
                body: formData,
            });

            // Since 'no-cors' mode is used, we cannot directly check response.ok or response.status
            // We assume success if no network error occurred.
            setSubmitMessage('Your inquiry has been sent successfully!');
            setEmail(''); // Clear form fields
            setMessage('');
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitMessage('There was an error sending your inquiry. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Intersection Observer logic for fade-in animations
    const observedSections = useRef(new Set());
    const setSectionRef = useCallback((node) => {
        if (node) {
            observedSections.current.add(node);
        }
    }, []);

    const handleSectionIntersect = useCallback((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('fade-in-up-active');
                // Remove initial opacity and transform classes if they were set for the animation
                entry.target.classList.remove('opacity-0', 'translate-y-10');
                if (entry.target.currentObserver) {
                    entry.target.currentObserver.unobserve(entry.target);
                }
            }
        });
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(handleSectionIntersect, {
            root: null,
            rootMargin: '0px',
            threshold: 0.1 // Trigger when 10% of the element is visible
        });

        observedSections.current.forEach(section => {
            if (section) {
                observer.observe(section);
                section.currentObserver = observer; // Store observer reference on the element
            }
        });

        return () => {
            observedSections.current.forEach(section => {
                if (section && section.currentObserver) {
                    section.currentObserver.unobserve(section);
                }
            });
            observer.disconnect();
            observedSections.current.clear();
        };
    }, [handleSectionIntersect]);

    return (
        <div className="min-h-screen bg-[#0A1128] text-[#F8F8F8] font-sans overflow-x-hidden">

            {/* Global Styles for animations (same as App.js for consistency) */}
            <style>
                {`
                .fade-in-up { opacity: 0; transform: translateY(20px); transition: opacity 0.8s ease-out, transform 0.8s ease-out; }
                .fade-in-up-active { opacity: 1; transform: translateY(0); }
                `}
            </style>

            {/* Section 1: Hero/Introduction - Blue Background */}
            <section
                className="relative py-20 text-center px-6 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url('https://i.imgur.com/CM0ZfGk.png')` }} /* Using the image from data analysis page as an example, replace if needed */
            >
                {/* Overlay for background image brightness */}
                <div className="absolute inset-0 bg-black bg-opacity-20"></div> {/* Changed opacity to 20% */}
                <div className="relative z-10 container mx-auto flex flex-col items-center justify-center">
                    {/* Added a div for the rounded black overlay */}
                    <div className="bg-black bg-opacity-60 p-8 rounded-lg max-w-4xl w-full">
                        <h1 className="text-5xl md:text-6xl font-extrabold text-[#C9B072] mb-6">
                            Virtual Assistance Services
                        </h1>
                        <p className="text-xl md:text-2xl text-[#F8F8F8] max-w-3xl mx-auto mb-10">
                            Offload your administrative tasks to our skilled virtual assistants and reclaim your valuable time. We provide reliable and efficient support tailored to your business needs, enabling you to focus on core operations.
                        </p>
                        <Link to="/get-a-quote" className="px-8 py-3 bg-[#4CAF50] text-[#F8F8F8] font-semibold text-lg rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90 inline-flex items-center justify-content-center">
                            Get a Virtual Assistance Quote
                        </Link>
                    </div>
                </div>
            </section>

            {/* Section 2: Core Services - Whitish Background */}
            <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0A1128] mb-12 fade-in-up" ref={setSectionRef}>
                        Our Virtual Assistance Expertise
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                        <div className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105" ref={setSectionRef} style={{ animationDelay: '0.1s' }}>
                            <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center">
                                <Briefcase size={48} />
                            </div>
                            <h3 className="text-2xl font-bold text-[#0A1128] mb-2">Administrative Support</h3>
                            <p className="text-gray-700">Email management, document preparation, data entry, and general office tasks.</p>
                        </div>
                        <div className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105" ref={setSectionRef} style={{ animationDelay: '0.2s' }}>
                            <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center">
                                <Calendar size={48} />
                            </div>
                            <h3 className="text-2xl font-bold text-[#0A1128] mb-2">Scheduling & Calendar Management</h3>
                            <p className="text-gray-700">Efficiently manage appointments, meetings, and travel itineraries.</p>
                        </div>
                        <div className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105" ref={setSectionRef} style={{ animationDelay: '0.3s' }}>
                            <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center">
                                <Users size={48} />
                            </div>
                            <h3 className="text-2xl font-bold text-[#0A1128] mb-2">Customer Service & Support</h3>
                            <p className="text-gray-700">Professional and timely customer support via email, chat, or phone.</p>
                        </div>
                        <div className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105" ref={setSectionRef} style={{ animationDelay: '0.4s' }}>
                            <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center">
                                <Megaphone size={48} />
                            </div>
                            <h3 className="text-2xl font-bold text-[#0A1128] mb-2">Social Media Assistance</h3>
                            <p className="text-gray-700">Content scheduling, community engagement, and basic social media management.</p>
                        </div>
                        <div className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105" ref={setSectionRef} style={{ animationDelay: '0.5s' }}>
                            <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center">
                                <Laptop size={48} />
                            </div>
                            <h3 className="text-2xl font-bold text-[#0A1128] mb-2">Research & Report Preparation</h3>
                            <p className="text-gray-700">Conducting online research and compiling professional reports.</p>
                        </div>
                        <div className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105" ref={setSectionRef} style={{ animationDelay: '0.6s' }}>
                            <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center">
                                <Settings size={48} />
                            </div>
                            <h3 className="text-2xl font-bold text-[#0A1128] mb-2">Customized Support</h3>
                            <p className="text-gray-700">Tailored assistance for unique business processes and project needs.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Section 3: How Can We Assist? / Contact Form - Blue Background */}
            <section
                className="py-20 border-t border-[#C9B072] text-center px-6 relative overflow-hidden"
                style={{
                    backgroundImage: 'linear-gradient(rgba(10, 17, 40, 0.9), rgba(10, 17, 40, 0.9)), url(https://i.imgur.com/F4Z9W90.png)', /* Replaced with your image URL */
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed', // Makes the background fixed while scrolling
                }}
            >
                <div className="container mx-auto max-w-2xl relative z-10"> {/* Added relative z-10 to keep content above background overlay */}
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#F8F8F8] mb-8 fade-in-up" ref={setSectionRef}>
                        How Can We Assist You?
                    </h2>
                    <p className="text-xl text-[#CCD2E3] mb-10 fade-in-up" ref={setSectionRef}>
                        Have specific needs or questions about our virtual assistance services? Fill out the form below, and we'll get back to you promptly.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6 text-left bg-[#1A2238] p-8 rounded-xl shadow-lg fade-in-up backdrop-blur-sm bg-opacity-80" ref={setSectionRef} style={{ animationDelay: '0.2s' }}> {/* Added backdrop-blur-sm and bg-opacity-80 */}
                        <div>
                            <label htmlFor="email" className="block text-xl font-medium text-[#F8F8F8] mb-2">
                                <Mail className="inline-block mr-2 text-[#4CAF50]" size={20} /> Your Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                name={GOOGLE_FORM_EMAIL_ENTRY_ID}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                placeholder="your.email@example.com"
                                className="w-full p-4 rounded-lg bg-[#0A1128] text-[#F8F8F8] border border-[#C9B072] focus:border-[#4CAF50] focus:ring focus:ring-[#4CAF50] focus:ring-opacity-50 transition duration-200"
                            />
                        </div>
                        <div>
                            <label htmlFor="message" className="block text-xl font-medium text-[#F8F8F8] mb-2">
                                <MessageSquare className="inline-block mr-2 text-[#4CAF50]" size={20} /> How can we help?
                            </label>
                            <textarea
                                id="message"
                                name={GOOGLE_FORM_MESSAGE_ENTRY_ID}
                                rows="6"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                required
                                placeholder="Describe your virtual assistance needs or questions here..."
                                className="w-full p-4 rounded-lg bg-[#0A1128] text-[#F8F8F8] border border-[#C9B072] focus:border-[#4CAF50] focus:ring focus:ring-[#4CAF50] focus:ring-opacity-50 transition duration-200"
                            ></textarea>
                        </div>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full px-8 py-4 bg-[#4CAF50] text-[#0A1128] font-bold text-xl rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90 flex items-center justify-center space-x-2"
                        >
                            {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                        </button>
                        {submitMessage && (
                            <p className={`mt-4 text-center ${submitMessage.includes('error') ? 'text-red-500' : 'text-green-500'}`}>
                                {submitMessage}
                            </p>
                        )}
                    </form>
                </div>
            </section>

            {/* Section 4: Call to Action - Whitish Background */}
            <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto max-w-3xl">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0A1128] mb-8 fade-in-up" ref={setSectionRef}>
                        Ready to Reclaim Your Time?
                    </h2>
                    <p className="text-xl text-[#333333] mb-10 fade-in-up" ref={setSectionRef}>
                        Let our virtual assistants empower your productivity and streamline your operations.
                    </p>
                    <Link to="/contact-us" className="px-12 py-4 bg-[#C9B072] text-[#0A1128] font-bold text-xl rounded-full shadow-2xl transition duration-300 transform hover:scale-105 hover:bg-opacity-90 animate-pulse inline-flex items-center justify-content-center fade-in-up" ref={setSectionRef}>
                        Partner with a Virtual Assistant
                    </Link>
                </div>
            </section>

        </div>
    );
};

export default VirtualAssistance;
